/**
 * Tax: dashboard, tax-year report with drill-down (total → property → category →
 * transaction), exports, and Making Tax Digital quarterly obligations.
 * Everything here is record-keeping and reporting — not tax advice.
 */
import {
  ACCOUNTING_BASES,
  HMRC_EXPENSE_CATEGORIES,
  HMRC_INCOME_CATEGORIES,
  NOT_TAX_ADVICE,
} from "@shared/constants.ts";
import type { Summary } from "@shared/finance/ledger.ts";
import { v } from "@shared/validation.ts";
import { Alert, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router";
import { DateField, TextField } from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  EmptyState,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, withQuery } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { useLookups } from "../../lib/lookups.ts";
import { useApi } from "../../lib/query.ts";
import { useUrlState } from "../../lib/url-state.ts";

interface Figures {
  income: Record<string, number>;
  expenses: Record<string, number>;
  adjustments: { costOfReplacingDomesticItems: number };
  excluded: { capitalPence: number; nonAllowablePence: number; nonPropertyReceiptsPence: number };
}

interface TaxReport {
  year: number;
  label: string;
  range: { from: string; to: string };
  basis: string;
  owner: { id: string; name: string; ownerType: string } | null;
  summary: Summary;
  figures: Figures;
  properties: Array<{ propertyId: string; label: string; summary: Summary; figures: Figures }>;
  disclaimer: string;
  transactionCount: number;
}

function Disclaimer() {
  return (
    <Alert variant="warning" className="disclaimer small">
      <strong>Not tax advice.</strong> {NOT_TAX_ADVICE}
    </Alert>
  );
}

function useTaxScope() {
  const { get, set } = useUrlState();
  const years = useApi<{ current: number; years: Array<{ year: number; label: string }> }>("/api/tax/years");
  const year = Number(get("year")) || years.data?.current || null;
  return {
    year,
    ownerId: get("ownerId"),
    propertyId: get("propertyId"),
    years: years.data?.years ?? [],
    set,
  };
}

function TaxScopeBar({
  scope,
  owners,
}: {
  scope: ReturnType<typeof useTaxScope>;
  owners: Array<{ id: string; label: string; ownerType: string }>;
}) {
  const owner = owners.find((o) => o.id === scope.ownerId);
  return (
    <Card className="mb-4 no-print">
      <Card.Body className="d-flex flex-wrap gap-2 align-items-center">
        <Form.Select
          size="sm"
          style={{ width: "auto" }}
          aria-label={owner?.ownerType === "company" ? "Accounting year ending in" : "Tax year"}
          value={scope.year ?? ""}
          onChange={(e) => scope.set({ year: e.target.value, propertyId: null })}
        >
          {owner?.ownerType === "company"
            ? scope.years.map((y) => (
                <option key={y.year} value={y.year + 1}>
                  Accounting year ending {y.year + 1}
                </option>
              ))
            : scope.years.map((y) => (
                <option key={y.year} value={y.year}>
                  Tax year {y.label}
                </option>
              ))}
        </Form.Select>
        <Form.Select
          size="sm"
          style={{ width: "auto" }}
          aria-label="Owner"
          value={scope.ownerId}
          onChange={(e) => scope.set({ ownerId: e.target.value, propertyId: null })}
        >
          <option value="">Whole portfolio (all owners)</option>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
              {o.ownerType === "company" ? " (company)" : ""}
            </option>
          ))}
        </Form.Select>
      </Card.Body>
    </Card>
  );
}

function HmrcTable({ figures, drill }: { figures: Figures; drill: (hmrcKey: string) => string }) {
  const row = (key: string, label: string, pence: number, kind: "income" | "expense") => (
    <tr key={key}>
      <td>
        {label}
        <div className="small text-muted font-monospace">{key.split(".")[1]}</div>
      </td>
      <td className="text-end">
        {pence ? (
          <Link to={drill(key)}>
            <Money pence={pence} kind={kind} />
          </Link>
        ) : (
          <span className="text-muted">£0.00</span>
        )}
      </td>
    </tr>
  );
  const incomeTotal = Object.values(figures.income).reduce((a, b) => a + b, 0);
  const expenseTotal = Object.values(figures.expenses).reduce((a, b) => a + b, 0);
  return (
    <Table className="mb-0 align-middle">
      <tbody>
        <tr className="table-light">
          <th colSpan={2}>Property income</th>
        </tr>
        {HMRC_INCOME_CATEGORIES.map((c) =>
          row(`income.${c.value}`, c.label, figures.income[c.value] ?? 0, "income"),
        )}
        <tr className="fw-semibold">
          <td>Total income</td>
          <td className="text-end">
            <Money pence={incomeTotal} kind="income" />
          </td>
        </tr>
        <tr className="table-light">
          <th colSpan={2}>Expenses (by the category you assigned)</th>
        </tr>
        {HMRC_EXPENSE_CATEGORIES.map((c) =>
          row(`expenses.${c.value}`, c.label, figures.expenses[c.value] ?? 0, "expense"),
        )}
        <tr className="fw-semibold">
          <td>Total expenses incl. finance costs</td>
          <td className="text-end">
            <Money pence={expenseTotal} kind="expense" />
          </td>
        </tr>
        <tr className="table-light">
          <th colSpan={2}>Annual adjustments & excluded items (for information)</th>
        </tr>
        {row(
          "adjustments.costOfReplacingDomesticItems",
          "Replacement of domestic items",
          figures.adjustments.costOfReplacingDomesticItems,
          "expense",
        )}
        <tr>
          <td>Capital expenditure (not deducted)</td>
          <td className="text-end">
            <Money pence={figures.excluded.capitalPence} />
          </td>
        </tr>
        <tr>
          <td>Private / non-allowable</td>
          <td className="text-end">
            <Money pence={figures.excluded.nonAllowablePence} />
          </td>
        </tr>
        <tr>
          <td>Non-property receipts</td>
          <td className="text-end">
            <Money pence={figures.excluded.nonPropertyReceiptsPence} />
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

export function TaxDashboardPage() {
  const lookups = useLookups();
  const scope = useTaxScope();
  const f = useFormat();
  const report = useApi<TaxReport>(
    scope.year ? "/api/tax/report" : null,
    { year: scope.year, ownerId: scope.ownerId },
    { keepPrevious: true },
  );
  const individuals = (lookups?.owners ?? []).filter((o) => o.ownerType === "individual");
  return (
    <>
      <PageHeader
        title="Tax"
        subtitle="UK tax-year figures for your property income"
        actions={
          <>
            <Link
              className="btn btn-primary"
              to={withQuery("/tax/self-assessment", { ownerId: scope.ownerId })}
            >
              Self Assessment (SA105)
            </Link>
            <Link className="btn btn-outline-primary" to={withQuery("/tax/mtd", { ownerId: scope.ownerId })}>
              Quarterly position
            </Link>
            <Link
              className="btn btn-outline-secondary"
              to={withQuery("/tax/report", { year: scope.year, ownerId: scope.ownerId })}
            >
              Tax-year report
            </Link>
          </>
        }
      />
      <Disclaimer />
      <TaxScopeBar scope={scope} owners={lookups?.owners ?? []} />
      <QueryState query={report}>
        {(r) => (
          <>
            <p className="small text-muted">
              {r.label} · {f.date(r.range.from)} – {f.date(r.range.to)} · {f.label(ACCOUNTING_BASES, r.basis)}
              {r.owner ? ` · ${r.owner.name}'s share` : ""}
            </p>
            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}>
                <StatCard
                  label="Property income"
                  value={<Money pence={r.summary.incomePence} kind="income" />}
                  tone="income"
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  label="Operating expenses"
                  value={<Money pence={r.summary.operatingExpensesPence} kind="expense" />}
                  tone="expense"
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  label="Finance costs"
                  value={<Money pence={r.summary.financeCostsPence} />}
                  hint="Shown separately (restricted relief for residential)"
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  label="Net before finance costs"
                  value={
                    <Money
                      pence={
                        r.summary.incomePence -
                        r.summary.operatingExpensesPence -
                        r.summary.replacementItemsPence
                      }
                      kind="net"
                    />
                  }
                  tone="net"
                />
              </Col>
            </Row>
            <Row>
              <Col lg={7}>
                <Section title="By HMRC category" flush>
                  <HmrcTable
                    figures={r.figures}
                    drill={(k) =>
                      withQuery("/reports/transactions", {
                        from: r.range.from,
                        to: r.range.to,
                        preset: "custom",
                        ownerId: scope.ownerId,
                        hmrc: k,
                      })
                    }
                  />
                </Section>
              </Col>
              <Col lg={5}>
                <Section
                  title="Returns and quarterly figures"
                  actions={
                    <Link to="/tax/mtd" className="small">
                      Quarterly position
                    </Link>
                  }
                >
                  {individuals.length === 0 ? (
                    <p className="small text-muted mb-0">
                      Add individual owners to see Making Tax Digital quarterly periods.
                    </p>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {individuals.map((o) => (
                        <li key={o.id} className="mb-2">
                          {o.label}:{" "}
                          <Link to={withQuery("/tax/self-assessment", { ownerId: o.id })}>
                            Self Assessment
                          </Link>
                          {" · "}
                          <Link to={withQuery("/tax/mtd", { ownerId: o.id, year: scope.year })}>
                            quarterly position
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="small text-muted mt-3 mb-0">
                    MTD for Income Tax applies to individuals with qualifying income over £50,000 from April
                    2026, over £30,000 from April 2027 and over £20,000 from April 2028. If you're signed up
                    to MTD, quarterly updates must be sent from MTD-compatible software: HMRC has no online
                    form to type them into. This app shows the figures; it doesn't send them yet.
                  </p>
                </Section>
              </Col>
            </Row>
          </>
        )}
      </QueryState>
    </>
  );
}

export function TaxReportPage() {
  const lookups = useLookups();
  const scope = useTaxScope();
  const f = useFormat();
  const { can } = useAuth();
  const report = useApi<TaxReport>(
    scope.year ? "/api/tax/report" : null,
    { year: scope.year, ownerId: scope.ownerId },
    { keepPrevious: true },
  );
  return (
    <>
      <PageHeader
        title="Tax-year report"
        breadcrumbs={[{ label: "Tax", to: "/tax" }, { label: "Report" }]}
        actions={
          can("tax:export") && (
            <>
              <a
                className="btn btn-outline-secondary"
                href={withQuery("/api/tax/report/export", { year: scope.year, ownerId: scope.ownerId })}
              >
                <Icon name="download" size={16} /> Export CSV
              </a>
              <a
                className="btn btn-outline-secondary"
                href={withQuery("/api/tax/report/export", {
                  year: scope.year,
                  ownerId: scope.ownerId,
                  format: "json",
                })}
              >
                JSON
              </a>
              <Button variant="outline-secondary" onClick={() => window.print()}>
                Print / PDF
              </Button>
            </>
          )
        }
      />
      <Disclaimer />
      <TaxScopeBar scope={scope} owners={lookups?.owners ?? []} />
      <QueryState query={report}>
        {(r) => {
          const property = scope.propertyId
            ? r.properties.find((p) => p.propertyId === scope.propertyId)
            : null;
          const figures = property?.figures ?? r.figures;
          const drillBase = {
            from: r.range.from,
            to: r.range.to,
            preset: "custom",
            ownerId: scope.ownerId,
            propertyId: property?.propertyId,
          };
          return (
            <>
              <Card className="mb-4">
                <Card.Body>
                  <h2 className="h5 mb-1">
                    {r.label}
                    {r.owner ? ` — ${r.owner.name}` : " — whole portfolio"}
                  </h2>
                  <div className="small text-muted">
                    {f.date(r.range.from)} to {f.date(r.range.to)} · {f.label(ACCOUNTING_BASES, r.basis)} ·{" "}
                    {r.transactionCount} transactions
                    {r.owner &&
                      " · jointly owned properties are included at this owner's share on each transaction date"}
                  </div>
                </Card.Body>
              </Card>
              <Section title="Property-by-property" flush>
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th className="text-end">Income</th>
                      <th className="text-end">Operating expenses</th>
                      <th className="text-end">Finance costs</th>
                      <th className="text-end">Replacement items</th>
                      <th className="text-end">Net</th>
                      <th className="text-end">Capital</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.properties.map((p) => (
                      <tr
                        key={p.propertyId}
                        className={p.propertyId === scope.propertyId ? "table-primary" : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start"
                            onClick={() =>
                              scope.set({
                                propertyId: p.propertyId === scope.propertyId ? null : p.propertyId,
                              })
                            }
                          >
                            {p.label}
                          </button>
                        </td>
                        <td className="text-end">
                          <Money pence={p.summary.incomePence} kind="income" />
                        </td>
                        <td className="text-end">
                          <Money pence={p.summary.operatingExpensesPence} kind="expense" />
                        </td>
                        <td className="text-end">
                          <Money pence={p.summary.financeCostsPence} />
                        </td>
                        <td className="text-end">
                          <Money pence={p.summary.replacementItemsPence} />
                        </td>
                        <td className="text-end fw-semibold">
                          <Money pence={p.summary.netPence} kind="net" />
                        </td>
                        <td className="text-end text-muted">
                          <Money pence={p.summary.capitalPence} />
                        </td>
                      </tr>
                    ))}
                    {r.properties.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <EmptyState icon="percent" title="No transactions in this period">
                            Record income and expenses to build your tax-year figures.
                          </EmptyState>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold">
                      <td>Total</td>
                      <td className="text-end">
                        <Money pence={r.summary.incomePence} kind="income" />
                      </td>
                      <td className="text-end">
                        <Money pence={r.summary.operatingExpensesPence} kind="expense" />
                      </td>
                      <td className="text-end">
                        <Money pence={r.summary.financeCostsPence} />
                      </td>
                      <td className="text-end">
                        <Money pence={r.summary.replacementItemsPence} />
                      </td>
                      <td className="text-end">
                        <Money pence={r.summary.netPence} kind="net" />
                      </td>
                      <td className="text-end">
                        <Money pence={r.summary.capitalPence} />
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </Section>
              <Section
                title={
                  property ? `Category breakdown — ${property.label}` : "Category breakdown — all properties"
                }
                actions={
                  property && (
                    <Button size="sm" variant="link" onClick={() => scope.set({ propertyId: null })}>
                      Show all properties
                    </Button>
                  )
                }
                flush
              >
                <HmrcTable
                  figures={figures}
                  drill={(k) => withQuery("/reports/transactions", { ...drillBase, hmrc: k })}
                />
              </Section>
              <p className="small text-muted">
                Select a property above for its breakdown, then any amount to see the individual transactions.
              </p>
            </>
          );
        }}
      </QueryState>
    </>
  );
}

type UkProperty = { ukProperty: { income: Record<string, string>; expenses: Record<string, string> } };

interface MtdOverview {
  owner: {
    id: string;
    name: string;
    mtdStatus: string;
    quarterlyPeriodType: string;
    consolidatedExpenses: boolean;
  };
  label: string;
  periodType: "standard" | "calendar";
  obligations: Array<{
    quarter: number;
    from: string;
    to: string;
    deadline: string;
    cumulative: { from: string; to: string };
    status: string;
    payload: UkProperty;
    quarterOnly: { payload: UkProperty };
    latestSubmission: {
      submittedAt: string;
      submittedOnDate: string | null;
      submittedByName: string;
      hmrcReceiptId: string | null;
    } | null;
    changedSinceSubmission: boolean;
  }>;
  annual: {
    payload: UkProperty;
    finalDeclarationDeadline: string;
  };
  generatedAt: string;
  note: string;
}

const submissionSchema = v.object({
  submittedOn: v.date(),
  hmrcReceiptId: v.string({ max: 100 }).optional(),
  notes: v.string({ max: 2000 }).default(""),
});

function PayloadList({ payload }: { payload: UkProperty }) {
  const entries = [
    ...Object.entries(payload.ukProperty.income),
    ...Object.entries(payload.ukProperty.expenses),
  ];
  if (entries.length === 0) return <span className="text-muted small">No figures</span>;
  return (
    <dl className="small mb-0 row">
      {entries.map(([k, val]) => (
        <div key={k} className="col-12 d-flex justify-content-between">
          <dt className="fw-normal font-monospace">{k}</dt>
          <dd className="mb-0">£{val}</dd>
        </div>
      ))}
    </dl>
  );
}

export function MtdPage() {
  const lookups = useLookups();
  const scope = useTaxScope();
  const f = useFormat();
  const { get } = useUrlState();
  const individuals = (lookups?.owners ?? []).filter((o) => o.ownerType === "individual");
  const ownerId = scope.ownerId || individuals[0]?.id || "";
  const periodType = get("periodType") || null;
  const params = { ownerId, year: scope.year, periodType };
  const q = useApi<MtdOverview>(ownerId && scope.year ? "/api/tax/mtd" : null, params);
  return (
    <>
      <PageHeader
        title="Quarterly position"
        subtitle="Income and expenses by quarter, using HMRC's quarterly update field names"
        breadcrumbs={[{ label: "Tax", to: "/tax" }, { label: "Quarterly position" }]}
        actions={
          ownerId && (
            <>
              <Can permission="tax:export">
                <a
                  className="btn btn-outline-secondary"
                  href={withQuery("/api/tax/mtd/export", { ...params, format: "csv" })}
                >
                  <Icon name="download" size={16} /> Export CSV
                </a>
                <a className="btn btn-outline-secondary" href={withQuery("/api/tax/mtd/export", params)}>
                  JSON
                </a>
              </Can>
              <Button variant="outline-secondary" onClick={() => window.print()}>
                Print / PDF
              </Button>
            </>
          )
        }
      />
      <Disclaimer />
      {individuals.length === 0 ? (
        <EmptyState
          icon="owner"
          title="No individual owners"
          action={
            <Link className="btn btn-primary" to="/owners/new">
              Add owner
            </Link>
          }
        >
          Quarterly figures are worked out per individual landlord. Add an owner and set their property
          shares.
        </EmptyState>
      ) : (
        <>
          <Card className="mb-4 no-print">
            <Card.Body className="d-flex flex-wrap gap-2">
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                aria-label="Owner"
                value={ownerId}
                onChange={(e) => scope.set({ ownerId: e.target.value })}
              >
                {individuals.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                aria-label="Tax year"
                value={scope.year ?? ""}
                onChange={(e) => scope.set({ year: e.target.value })}
              >
                {scope.years.map((y) => (
                  <option key={y.year} value={y.year}>
                    Tax year {y.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                aria-label="Quarters"
                value={q.data?.periodType ?? periodType ?? ""}
                onChange={(e) => scope.set({ periodType: e.target.value })}
              >
                <option value="standard">Standard quarters (6 April to 5 July …)</option>
                <option value="calendar">Calendar quarters (1 April to 30 June …)</option>
              </Form.Select>
            </Card.Body>
          </Card>
          <QueryState query={q}>
            {(m) => (
              <>
                <Alert variant="info" className="small">
                  {m.note} If you're signed up to Making Tax Digital, HMRC has no online form for typing in
                  quarterly updates: they must be sent from MTD-compatible software.
                </Alert>
                <p className="small text-muted">
                  {m.owner.name}'s share of jointly owned properties · figures as at{" "}
                  {f.dateTime(m.generatedAt)}
                </p>
                <Section title={`${m.owner.name}: ${m.label}`} flush>
                  <Table responsive className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Quarter</th>
                        <th>Period</th>
                        <th>Deadline</th>
                        <th>This quarter</th>
                        <th>Year to date (as sent to HMRC)</th>
                        <th>Status</th>
                        <th className="no-print" />
                      </tr>
                    </thead>
                    <tbody>
                      {m.obligations.map((o) => (
                        <tr key={o.quarter}>
                          <td>Q{o.quarter}</td>
                          <td className="small">
                            {f.date(o.from)} – {f.date(o.to)}
                          </td>
                          <td className="small">{f.date(o.deadline)}</td>
                          <td style={{ minWidth: 220 }}>
                            <PayloadList payload={o.quarterOnly.payload} />
                          </td>
                          <td style={{ minWidth: 220 }}>
                            <PayloadList payload={o.payload} />
                          </td>
                          <td>
                            <StatusBadge
                              status={o.status}
                              label={o.status === "amend" ? "changed since recorded" : o.status}
                            />
                            {o.latestSubmission && (
                              <div className="small text-muted">
                                Submitted{" "}
                                {f.date(o.latestSubmission.submittedOnDate ?? o.latestSubmission.submittedAt)}
                                {o.latestSubmission.hmrcReceiptId
                                  ? ` · ${o.latestSubmission.hmrcReceiptId}`
                                  : ""}
                              </div>
                            )}
                          </td>
                          <td className="text-end no-print">
                            <Can permission="tax:submit">
                              <ModalFormButton
                                label={o.latestSubmission ? "Record again" : "Record as submitted"}
                                title={`Record Q${o.quarter} update as submitted`}
                                schema={submissionSchema}
                                defaults={{ submittedOn: "", hmrcReceiptId: "", notes: "" }}
                                onSubmit={(val) =>
                                  api.post("/api/tax/mtd/submissions", {
                                    ...val,
                                    ownerId,
                                    year: scope.year,
                                    quarter: o.quarter,
                                  })
                                }
                              >
                                {() => (
                                  <>
                                    <p className="small">
                                      This keeps a copy of the current year-to-date figures, so you'll be told
                                      if records in this period change later. It does not submit to HMRC.
                                    </p>
                                    <DateField name="submittedOn" label="Date submitted to HMRC" required />
                                    <TextField
                                      name="hmrcReceiptId"
                                      label="HMRC receipt / correlation ID (optional)"
                                    />
                                    <TextField name="notes" label="Notes" />
                                  </>
                                )}
                              </ModalFormButton>
                            </Can>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Section>
                <Section title="Full tax year">
                  <PayloadList payload={m.annual.payload} />
                  <p className="small text-muted mt-2 mb-0">
                    For your Self Assessment return, see the{" "}
                    <Link to={withQuery("/tax/self-assessment", { ownerId, year: scope.year })}>
                      UK property pages (SA105)
                    </Link>
                    . Online returns are due by {f.date(m.annual.finalDeclarationDeadline)}.
                  </p>
                </Section>
              </>
            )}
          </QueryState>
        </>
      )}
    </>
  );
}
